import type { PostEditSchemaEditPayload } from '@repo/schemas/post/postEdit';
import type { PostPublic, PostMutationOptions } from '@repo/types/posts';
import type { InfiniteData } from '@tanstack/react-query';

import { mutationOptions, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';

import { useErrorMessage } from '#error-message/useErrorMessage';
import { GroupKey } from '#group/keys';

import { PostKey } from './keys';
import { PostService } from './postService';

type InfinitePosts = InfiniteData<PostPublic[], unknown>;

function updateSinglePost(queryClient: QueryClient, post: PostPublic) {
  queryClient.setQueryData([PostKey.POSTS, post.id], post);
}

function updateFlatPosts(queryClient: QueryClient, post: PostPublic) {
  queryClient.setQueryData([PostKey.POSTS], (curr?: PostPublic[]) => {
    if (!curr) {
      return;
    }
    return curr.map((p) => (p.id === post.id ? post : p));
  });
}

function updateInfinitePosts(queryClient: QueryClient, queryKey: readonly unknown[], post: PostPublic, shouldRemove: boolean) {
  queryClient.setQueryData(queryKey, (curr?: InfinitePosts) => {
    if (!curr) {
      return;
    }
    if (shouldRemove) {
      return {
        ...curr,
        pages: curr.pages.map((page) => page.filter((p) => p.id !== post.id)),
      };
    }
    return {
      ...curr,
      pages: curr.pages.map((page) => page.map((p) => (p.id === post.id ? post : p))),
    };
  });
}

function editGroupPostMutationOptions(queryClient: QueryClient, groupId: number) {
  return mutationOptions({
    mutationFn: (data: PostEditSchemaEditPayload) => PostService.editPost(data).then((response) => response.data),
    onSuccess: (data) => {
      const shouldRemoveFromGroup: boolean = data.global;

      updateSinglePost(queryClient, data);
      updateFlatPosts(queryClient, data);
      updateInfinitePosts(queryClient, [GroupKey.GROUP_POSTS_INFINITE, groupId], data, shouldRemoveFromGroup);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PostKey.POSTS, variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: [PostKey.POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [GroupKey.GROUP_POSTS_INFINITE, groupId],
      });
    },
  });
}

function editPostMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (data: PostEditSchemaEditPayload) => PostService.editPost(data).then((response) => response.data),
    onSuccess: (data) => {
      const shouldRemoveFromGlobal: boolean = !data.global;

      updateSinglePost(queryClient, data);
      updateFlatPosts(queryClient, data);
      updateInfinitePosts(queryClient, [PostKey.POSTS_INFINITE], data, shouldRemoveFromGlobal);

      if (shouldRemoveFromGlobal) {
        for (const group of data.groups) {
          queryClient.invalidateQueries({
            queryKey: [GroupKey.GROUP_POSTS_INFINITE, group.id],
          });
        }
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PostKey.POSTS, variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: [PostKey.POSTS],
      });
    },
  });
}

export function useEditPost(options?: PostMutationOptions) {
  const { groupId } = options ?? {};
  const queryClient = useQueryClient();
  const editPostMutation = useMutation(groupId ? editGroupPostMutationOptions(queryClient, groupId) : editPostMutationOptions(queryClient));

  const errorMessage = useErrorMessage(editPostMutation.error);

  return {
    ...editPostMutation,
    errorMessage,
  };
}
